import { useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import { useCompanies, useSession, MODULE_NAV } from "./lib/access";
import { SignIn } from "./pages/SignIn";
import { Wordmark } from "./components/Wordmark";
import { NoAccess } from "./pages/NoAccess";
import { Home } from "./pages/Home";
import { Security } from "./pages/Security";
import { CompanySwitcher } from "./components/CompanySwitcher";
import { AcceptInvite } from "./pages/AcceptInvite";
import { People } from "./pages/People";
import { Permissions } from "./pages/Permissions";
import { Audit } from "./pages/Audit";
import { Departments } from "./pages/Departments";
import { Settings } from "./pages/Settings";
import { Calls } from "./pages/Calls";
import { applyBrand } from "./lib/brand";
import { ImpersonationBanner } from "./components/ImpersonationBanner";

const ACTIVE_KEY = "hauzisha.activeCompany";

export function App() {
  const { session, loading } = useSession();
  const { companies, error } = useCompanies(session);
  const [activeId, setActiveId] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_KEY),
  );
  type View = "home" | "calls" | "people" | "departments" | "permissions" | "activity" | "settings" | "security";
  const [view, setView] = useState<View>("home");

  useEffect(() => {
    if (activeId) localStorage.setItem(ACTIVE_KEY, activeId);
  }, [activeId]);

  const brandHex = companies?.find((c) => c.id === activeId)?.brandHex
    ?? companies?.[0]?.brandHex ?? null;
  useEffect(() => { applyBrand(brandHex); }, [brandHex]);

  const inviteToken = new URLSearchParams(window.location.search).get("token");

  if (loading) return <main className="auth"><p className="muted">Loading…</p></main>;
  if (inviteToken) {
    return <AcceptInvite token={inviteToken} onAccepted={() => window.location.replace("/")} />;
  }
  if (!session) return <SignIn />;
  if (!companies) return <main className="auth"><p className="muted">Loading…</p></main>;
  if (error) return <main className="auth"><p className="status">{error}</p></main>;
  if (companies.length === 0) return <NoAccess email={session.user.email} />;

  // A remembered company the user no longer belongs to must not be trusted.
  const active = companies.find((c) => c.id === activeId) ?? companies[0]!;

  return (
    <>
      <ImpersonationBanner companyName={active.name} />
    <div className="shell">
      <aside>
        <Wordmark height={38} />
        <span className="workspace">{active.displayName ?? active.name}</span>
        <CompanySwitcher companies={companies} active={active} onChange={setActiveId} />
        <nav>
          <button className={view === "home" ? "on" : ""} onClick={() => setView("home")}>
            Overview
          </button>
          {MODULE_NAV.filter((m) => active.modules.includes(m.key)).map((m) =>
            m.key === "call_tracking" ? (
              <button key={m.key} className={view === "calls" ? "on" : ""}
                onClick={() => setView("calls")}>
                {m.label}
              </button>
            ) : (
              <button key={m.key} disabled title="Available in a later phase">
                {m.label}
              </button>
            ),
          )}
          <div className="nav-sep" />
          <button className={view === "people" ? "on" : ""} onClick={() => setView("people")}>
            People
          </button>
          {active.role !== "staff" && (
            <button className={view === "departments" ? "on" : ""}
              onClick={() => setView("departments")}>
              Departments
            </button>
          )}
          {active.role === "super_admin" && (
            <button className={view === "permissions" ? "on" : ""}
              onClick={() => setView("permissions")}>
              Permissions
            </button>
          )}
          {active.role !== "staff" && (
            <button className={view === "activity" ? "on" : ""} onClick={() => setView("activity")}>
              Activity
            </button>
          )}
          {active.role === "super_admin" && (
            <button className={view === "settings" ? "on" : ""} onClick={() => setView("settings")}>
              Settings
            </button>
          )}
          <button className={view === "security" ? "on" : ""} onClick={() => setView("security")}>
            Security
          </button>
        </nav>
        <button className="btn ghost small" onClick={() => void supabase.auth.signOut()}>
          Sign out
        </button>
      </aside>
      <main>
        {view === "home" && <Home company={active} />}
        {view === "people" && <People company={active} meId={session.user.id} />}
        {view === "permissions" && <Permissions company={active} />}
        {view === "calls" && <Calls company={active} />}
        {view === "departments" && <Departments company={active} />}
        {view === "activity" && <Audit company={active} />}
        {view === "settings" && (
          <Settings company={active} onSaved={() => window.location.reload()} />
        )}
        {view === "security" && <Security />}
      </main>
    </div>
    </>
  );
}
